/**
 * Auth API module — Screenista advertiser auth endpoints.
 */
import { httpClient } from '@/shared/api/httpClient';
import { endpoints } from '@/shared/api/endpoints';

export type LoginPayload = {
  identifier: string;
  password: string;
  rememberMe?: boolean;
};

export type LoginOtpPayload = {
  identifier: string;
};

export type VerifyLoginOtpPayload = {
  identifier: string;
  otp: string;
  rememberMe?: boolean;
};

export type SignupPayload = {
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  mobileCountryCode: string;
  accountType: string;
  referralCode?: string;
};

export type AccountTypeOption = {
  key: string;
  label: string;
  description?: string;
};

export type VerifyEmailOtpPayload = {
  sessionId: string;
  emailOtp: string;
};

export type VerifyPhoneSignupPayload = {
  sessionId: string;
  mobileNumber: string;
  phoneOtp: string;
};

export type ResendSignupOtpPayload = {
  sessionId: string;
  type: 'SIGNUP_EMAIL' | 'SIGNUP_PHONE';
};

export type SetPasswordPayload = {
  password: string;
  sessionId: string;
};

export type VerifyPanPayload = {
  pan_number: string;
  pan_name: string;
  sessionId: string;
};

export type VerifyGstPayload = {
  gst_number: string;
  sessionId: string;
};

export type ForgotPasswordPayload = {
  identifier: string;
};

export type VerifyForgotOtpPayload = {
  identifier: string;
  otp: string;
};

export type ResetPasswordPayload = {
  email: string;
  resetToken: string;
  newPassword: string;
  confirmPassword: string;
};

export async function loginRequest(payload: LoginPayload) {
  const { data } = await httpClient.post(endpoints.auth.login, payload);
  return data;
}

export async function sendLoginOtpRequest(payload: LoginOtpPayload) {
  const { data } = await httpClient.post(endpoints.auth.loginSendOtp, payload);
  return data;
}

export async function verifyLoginOtpRequest(payload: VerifyLoginOtpPayload) {
  const { data } = await httpClient.post(endpoints.auth.loginVerifyOtp, payload);
  return data;
}

export async function signupRequest(payload: SignupPayload) {
  const { data } = await httpClient.post(endpoints.auth.signup, payload);
  return data;
}

export async function fetchAccountTypesRequest() {
  const { data } = await httpClient.get(endpoints.auth.fetchAccountType);
  return data as { data?: AccountTypeOption[] };
}

export async function verifyEmailOtpRequest(payload: VerifyEmailOtpPayload) {
  const { data } = await httpClient.post(endpoints.auth.verifyEmailOtp, payload);
  return data;
}

export async function verifyPhoneSignupRequest(payload: VerifyPhoneSignupPayload) {
  const { data } = await httpClient.post(endpoints.auth.verifyPhoneSignup, payload);
  return data;
}

export async function resendSignupOtpRequest(payload: ResendSignupOtpPayload) {
  const { data } = await httpClient.post(endpoints.auth.resendOtp, payload);
  return data;
}

export async function fetchOnboardingFieldsRequest(roleName: string) {
  const { data } = await httpClient.get(endpoints.auth.getByRole, {
    params: { roleName },
  });
  return data;
}

export async function setProfileInfoRequest(payload: Record<string, unknown>) {
  const { data } = await httpClient.post(endpoints.auth.setProfileInfo, payload);
  return data;
}

export async function completeSignupRequest(payload: SetPasswordPayload) {
  const { data } = await httpClient.post(endpoints.auth.completeSignup, payload);
  return data;
}

export async function verifyPanRequest(payload: VerifyPanPayload) {
  const { data } = await httpClient.post(endpoints.kyc.verifyPan, payload);
  return data;
}

export async function verifyGstRequest(payload: VerifyGstPayload) {
  const { data } = await httpClient.post(endpoints.kyc.verifyGst, payload);
  return data;
}

export async function logoutRequest() {
  const { data } = await httpClient.post(endpoints.auth.logout);
  return data;
}

export async function forgotPasswordRequest(payload: ForgotPasswordPayload) {
  const { data } = await httpClient.post(endpoints.auth.forgotSendOtp, payload);
  return data;
}

export async function resendForgotOtpRequest(payload: ForgotPasswordPayload) {
  const { data } = await httpClient.post(endpoints.auth.forgotSendOtp, {
    ...payload,
    type: '',
  });
  return data;
}

export async function verifyForgotOtpRequest(payload: VerifyForgotOtpPayload) {
  const { data } = await httpClient.post(
    endpoints.auth.forgotVerifyOtp,
    payload,
  );
  return data as {
    message?: string;
    data?: { resetToken?: string; identifier?: string };
  };
}

export async function resetPasswordRequest(payload: ResetPasswordPayload) {
  const { data } = await httpClient.post(
    endpoints.auth.forgotResetPassword,
    payload,
  );
  return data;
}
